import { useMemo, useState } from "react";
import { Check, ArrowRight, Download } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { EXTRAS, OCCASIONS, PACKAGES, STEPS, UPGRADES } from "@/data/bespoke";

const FAQ = [
  {
    q: "How much does a bespoke song cost?",
    a: "Packages start at £99 for Bronze, £199 for Silver (our most popular) and £299 for Gold, with optional extras and upgrades available. Your enquiry form shows a live running total before you send anything.",
  },
  {
    q: "How does the process work?",
    a: "Five simple steps: a personal consultation, original lyrics written around your story, musical directions for you to preview and choose between, full studio production, and delivery of your finished song.",
  },
  {
    q: "What do I receive?",
    a: "Every package includes your completed song in MP3 and WAV formats. Silver adds a USB drive in a presentation box, and Gold adds closer collaboration, bespoke cover artwork and distribution to major music services.",
  },
  {
    q: "What occasions can you write for?",
    a: "Weddings, engagements, anniversaries, birthdays, graduations, milestones, celebrations, memorials, tributes and more — if it matters to you, it is worth turning into music.",
  },
  {
    q: "How do I pay?",
    a: "Nothing is charged when you enquire. Once we've confirmed your booking personally, payment is made via PayPal or bank transfer — full details are sent on confirmation.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function Bespoke() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    occasion: "Wedding",
    package: "silver",
    extras: [],
    message: "",
    contact_pref: "Email",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [voucherInput, setVoucherInput] = useState("");
  const [voucher, setVoucher] = useState(null);
  const [voucherMsg, setVoucherMsg] = useState("");

  const base = useMemo(() => {
    const p = PACKAGES.find((x) => x.id === form.package)?.price || 0;
    return p + form.extras.reduce((s, e) => s + (EXTRAS.find((x) => x.id === e)?.price || 0), 0);
  }, [form.package, form.extras]);

  const discount = voucher ? Math.round((base * voucher.percent) / 100) : 0;
  const total = base - discount;

  const applyVoucher = async () => {
    if (!voucherInput.trim()) return;
    setVoucherMsg("");
    try {
      const r = await api.post("/vouchers/check", { code: voucherInput });
      setVoucher({ code: r.data.code, percent: r.data.percent });
      toast.success(`Voucher ${r.data.code} applied — ${r.data.percent}% off`);
    } catch {
      setVoucher(null);
      setVoucherMsg("That code isn't valid or has expired.");
    }
  };

  const toggleExtra = (id) =>
    setForm((f) => ({ ...f, extras: f.extras.includes(id) ? f.extras.filter((x) => x !== id) : [...f.extras, id] }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const r = await api.post("/enquiries/bespoke", { ...form, voucher_code: voucher?.code || "" });
      setDone(true);
      toast.success(`Enquiry sent — quoted total £${r.data.total_quoted}. Check your inbox for confirmation.`);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-navy-900/70 border border-white/10 rounded-xl px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-gold-400/60 transition-colors";

  return (
    <div className="pt-28 pb-24" data-testid="bespoke-page">
      <Seo
        title="Personalised Songs & Bespoke Music from £99 - Open Horizon Records"
        description="Create a personalised song from your own story with Open Horizon Records. Original lyrics, professional music production and bespoke song packages from £99."
        path="/bespoke"
        jsonLd={FAQ_JSONLD}
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center max-w-3xl mx-auto">
          <p className="eyebrow mb-3">Bespoke Music</p>
          <h1 className="font-serif text-4xl sm:text-6xl text-slate-50 leading-tight">
            Your Story. Your Song. <span className="text-gold-gradient">Created With You.</span>
          </h1>
          <p className="mt-6 text-slate-300/85 font-light leading-relaxed">
            Every occasion. Every emotion. Every story deserves its own song.
          </p>
        </Reveal>

        {/* THE STORY */}
        <Reveal className="mt-16 max-w-3xl mx-auto" >
          <div className="rounded-3xl border border-gold-400/20 bg-navy-900/60 backdrop-blur-md p-8 sm:p-12" data-testid="bespoke-story">
            <p className="text-slate-300/90 font-light leading-relaxed">
              Some moments deserve more than a card, a photograph or another gift. They deserve
              something that captures the feeling, tells the story and can be played again and again.
            </p>
            <p className="mt-5 text-slate-300/90 font-light leading-relaxed">
              Imagine walking down the aisle to a song written just for the two of you. Giving your
              partner an anniversary surprise that remembers how it all began. Celebrating a birthday
              with music full of shared memories. Or honouring someone special with a tribute that
              says what ordinary words sometimes cannot.
            </p>
            <p className="mt-5 text-slate-300/90 font-light leading-relaxed">
              That is what we create at Open Horizon Records: a complete, original, professionally
              produced song, inspired by your own story and made especially for you.
            </p>
            <h2 className="font-serif text-2xl sm:text-3xl text-gold-gradient mt-10 text-center">
              A song for every chapter
            </h2>
            <p className="mt-5 text-slate-300/90 font-light leading-relaxed">
              From weddings, engagements and anniversaries to birthdays, family celebrations,
              graduations and milestones; from thank-you gifts and personal surprises to memorials,
              remembrance and heartfelt tributes — if it matters to you, it is worth turning into music.
            </p>
            <p className="mt-5 text-slate-300/90 font-light leading-relaxed">
              You do not need to be a songwriter or know anything about music production. Tell us about
              the people, memories, emotions and occasion. We will help shape those details into lyrics,
              explore musical directions with you and produce the finished song.
            </p>
          </div>
        </Reveal>

        {/* PACKAGES */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="packages-grid">
          {PACKAGES.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.12}>
              <div
                className={`relative h-full rounded-2xl p-8 flex flex-col border transition-colors duration-500 ${
                  p.id === "silver"
                    ? "border-gold-400/60 bg-gradient-to-b from-gold-400/10 to-navy-900/80 shadow-[0_0_50px_rgba(212,175,55,0.15)]"
                    : "border-white/10 bg-navy-900/60 hover:border-gold-400/30"
                }`}
                data-testid={`package-card-${p.id}`}
              >
                {p.id === "silver" && (
                  <span
                    className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.25em] uppercase px-4 py-1 rounded-full text-navy-950 font-semibold"
                    style={{ background: "linear-gradient(135deg,#F59E0B,#D4AF37 50%,#B38728)" }}
                    data-testid="most-popular-badge"
                  >
                    Most Popular
                  </span>
                )}
                <p className="eyebrow">{p.name}</p>
                <p className="mt-4 flex items-baseline gap-2">
                  <span className="font-serif text-5xl text-slate-50">£{p.price}</span>
                </p>
                <p className="mt-2 font-serif text-gold-200/90">{p.tagline}</p>
                <ul className="mt-6 space-y-3 flex-1">
                  {p.inclusions.map((inc) => (
                    <li key={inc} className="flex gap-3 text-sm text-slate-300 font-light leading-relaxed">
                      <Check size={15} className="text-gold-400 shrink-0 mt-0.5" />
                      {inc}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => {
                    setForm((f) => ({ ...f, package: p.id }));
                    document.getElementById("enquiry")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className={p.id === "silver" ? "btn-gold mt-8 w-full" : "btn-ghost-gold mt-8 w-full"}
                  data-testid={`package-select-${p.id}`}
                >
                  Choose {p.name}
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        {/* EXTRAS + UPGRADES */}
        <div className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Reveal>
            <div className="rounded-2xl border border-white/10 bg-navy-900/60 p-8 h-full" data-testid="extras-panel">
              <h2 className="font-serif text-2xl sm:text-3xl text-slate-50">Optional Extras & Bolt-Ons</h2>
              <ul className="mt-6 space-y-4">
                {EXTRAS.map((x) => (
                  <li key={x.id} className="flex items-center justify-between gap-4 border-b border-white/5 pb-4">
                    <span className="text-sm text-slate-300 font-light">{x.name}</span>
                    <span className="font-serif text-gold-300 whitespace-nowrap">£{x.price}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs text-slate-500 leading-relaxed">
                No double charging: Gold already includes artwork and distribution. Silver and Gold include a
                standard USB presentation box. Extras apply only where not already included.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="rounded-2xl border border-white/10 bg-navy-900/60 p-8 h-full" data-testid="upgrades-panel">
              <h2 className="font-serif text-2xl sm:text-3xl text-slate-50">Package Upgrades</h2>
              <ul className="mt-6 space-y-4">
                {UPGRADES.map((u) => (
                  <li key={`${u.from}-${u.to}`} className="flex items-center justify-between gap-4 border-b border-white/5 pb-4">
                    <span className="text-sm text-slate-300 font-light flex items-center gap-2">
                      {u.from} <ArrowRight size={13} className="text-gold-400" /> {u.to}
                    </span>
                    <span className="font-serif text-gold-300">£{u.price}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs text-slate-500 leading-relaxed">
                Wedding, anniversary, birthday, memorial and other occasion-themed presentation may include
                names, dates and personal messages. Box designs, colours and finishes are subject to
                availability; comparable alternatives may be supplied. More elaborate custom packaging may be
                quoted separately.
              </p>
            </div>
          </Reveal>
        </div>

        {/* HOW IT WORKS */}
        <div className="mt-24" data-testid="how-it-works">
          <Reveal className="text-center">
            <p className="eyebrow mb-3">The Journey</p>
            <h2 className="font-serif text-3xl sm:text-5xl text-slate-50">How It Works</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div className="relative rounded-2xl border border-white/10 bg-navy-900/60 p-6 h-full hover:border-gold-400/40 transition-colors duration-500">
                  <span className="font-serif text-4xl text-gold-gradient">{s.n}</span>
                  <h3 className="font-serif text-lg text-slate-100 mt-3">{s.title}</h3>
                  <p className="text-xs text-slate-400 mt-2 font-light leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="text-center mt-10">
            <p className="text-sm text-gold-200/80 font-light" data-testid="payment-note">
              Payment via PayPal or bank transfer — details sent on confirmation.
            </p>
          </Reveal>
        </div>

        {/* FAQ */}
        <div className="mt-24" data-testid="bespoke-faq">
          <Reveal className="text-center">
            <p className="eyebrow mb-3">Good To Know</p>
            <h2 className="font-serif text-3xl sm:text-5xl text-slate-50">Common Questions</h2>
          </Reveal>
          <div className="mt-12 max-w-3xl mx-auto space-y-4">
            {FAQ.map((f) => (
              <Reveal key={f.q}>
                <div className="rounded-2xl border border-white/10 bg-navy-900/60 p-6 sm:p-8">
                  <h3 className="font-serif text-lg text-slate-100">{f.q}</h3>
                  <p className="text-sm text-slate-400 mt-3 font-light leading-relaxed">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* ENQUIRY FORM */}
        <div id="enquiry" className="mt-24 scroll-mt-28" data-testid="bespoke-calculator">
          <Reveal>
            <div className="rounded-3xl border border-gold-400/20 bg-navy-900/70 backdrop-blur-md p-6 sm:p-10 lg:p-14">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
                <div className="lg:col-span-2">
                  <p className="eyebrow mb-3">Begin</p>
                  <h2 className="font-serif text-3xl sm:text-4xl text-slate-50">Start Your Enquiry</h2>
                  <p className="mt-4 text-sm text-slate-400 font-light leading-relaxed">
                    Tell us your story and choose your package. Your running total updates live —
                    nothing is charged now. We'll reply personally to confirm details.
                  </p>
                  <div className="mt-8 rounded-2xl border border-gold-400/25 bg-navy-950/60 p-6">
                    <p className="text-xs tracking-[0.25em] uppercase text-slate-500">Your quoted total</p>
                    <p className="font-serif text-5xl text-gold-gradient mt-2" data-testid="bespoke-total">
                      £{total}
                    </p>
                    <p className="text-xs text-slate-500 mt-3">
                      {PACKAGES.find((p) => p.id === form.package)?.name} package
                      {form.extras.length > 0 && ` + ${form.extras.length} extra${form.extras.length > 1 ? "s" : ""}`}
                    </p>
                    <div className="mt-5 pt-5 border-t border-white/10">
                      {voucher ? (
                        <div className="flex items-center justify-between gap-3" data-testid="voucher-applied">
                          <p className="text-xs text-gold-300">
                            Voucher {voucher.code} (−{voucher.percent}%) <span className="text-slate-400">saved £{discount}</span>
                          </p>
                          <button type="button" onClick={() => { setVoucher(null); setVoucherInput(""); setVoucherMsg(""); }}
                            className="text-[11px] text-slate-500 hover:text-red-400 transition-colors" data-testid="voucher-remove">
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div>
                          <div className="flex gap-2">
                            <input
                              value={voucherInput}
                              onChange={(e) => setVoucherInput(e.target.value.toUpperCase())}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyVoucher(); } }}
                              placeholder="Voucher code"
                              className="flex-1 min-w-0 bg-navy-900/70 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-gold-400/60 transition-colors uppercase"
                              data-testid="voucher-input"
                            />
                            <button type="button" onClick={applyVoucher}
                              className="shrink-0 rounded-lg border border-gold-400/40 px-4 py-2 text-xs tracking-widest uppercase text-gold-200 hover:bg-gold-400/10 transition-colors"
                              data-testid="voucher-apply-btn">
                              Apply
                            </button>
                          </div>
                          {voucherMsg && <p className="text-xs text-red-400 mt-2" data-testid="voucher-error">{voucherMsg}</p>}
                        </div>
                      )}
                    </div>
                  </div>
                  <a
                    href="/assets/Open-Horizon-Bespoke-Song-Questionnaire.pdf"
                    download
                    data-testid="questionnaire-download"
                    className="mt-5 flex items-center gap-4 rounded-2xl border border-white/10 bg-navy-950/60 p-5 hover:border-gold-400/50 transition-colors group"
                  >
                    <span className="w-11 h-11 shrink-0 rounded-full flex items-center justify-center border border-gold-400/40 text-gold-300 group-hover:bg-gold-400/10 transition-colors">
                      <Download size={17} />
                    </span>
                    <span>
                      <span className="block text-sm text-slate-100">Complete Song Questionnaire</span>
                      <span className="block text-xs text-slate-500 mt-0.5">
                        Prefer to write it all down? Download the PDF, fill it in at your own pace, then email it back to
                        openhorizonrecords@outlook.com — or mention it in your enquiry.
                      </span>
                    </span>
                  </a>
                </div>
                {done ? (
                  <div className="lg:col-span-3 flex flex-col items-center justify-center text-center py-16" data-testid="bespoke-success">
                    <span className="w-16 h-16 rounded-full flex items-center justify-center text-navy-950" style={{ background: "linear-gradient(135deg,#F59E0B,#D4AF37 50%,#B38728)" }}>
                      <Check size={28} />
                    </span>
                    <h3 className="font-serif text-2xl text-slate-50 mt-6">Thank you — your story is on its way to us.</h3>
                    <p className="text-sm text-slate-400 mt-3 max-w-md font-light">
                      We've emailed your confirmation and will be in touch personally via your preferred
                      contact method. Payment details (PayPal or bank transfer) follow on confirmation.
                    </p>
                    <p className="text-xs text-gold-200/90 bg-gold-400/10 border border-gold-400/30 rounded-xl px-4 py-3 mt-5 leading-relaxed max-w-md">
                      Please check your Spam/Junk folder for our confirmation email and mark it as safe,
                      so our reply about your song always reaches you.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={submit} className="lg:col-span-3 space-y-5" data-testid="bespoke-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <input required placeholder="Your name" className={inputCls} value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="bespoke-name-input" />
                      <input required type="email" placeholder="Email address" className={inputCls} value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="bespoke-email-input" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <input placeholder="Phone (optional)" className={inputCls} value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="bespoke-phone-input" />
                      <select className={inputCls} value={form.occasion}
                        onChange={(e) => setForm({ ...form, occasion: e.target.value })} data-testid="bespoke-occasion-select">
                        {OCCASIONS.map((o) => (
                          <option key={o} value={o} className="bg-navy-900">{o}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <p className="text-xs tracking-[0.25em] uppercase text-slate-500 mb-3">Package</p>
                      <div className="grid grid-cols-3 gap-3">
                        {PACKAGES.map((p) => (
                          <button
                            type="button"
                            key={p.id}
                            onClick={() => setForm({ ...form, package: p.id })}
                            data-testid={`bespoke-package-${p.id}`}
                            className={`rounded-xl border px-3 py-4 text-center transition-all duration-300 ${
                              form.package === p.id
                                ? "border-gold-400 bg-gold-400/10 text-gold-200"
                                : "border-white/10 text-slate-400 hover:border-gold-400/40"
                            }`}
                          >
                            <span className="block font-serif text-lg">{p.name}</span>
                            <span className="block text-xs mt-1">£{p.price}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs tracking-[0.25em] uppercase text-slate-500 mb-3">Optional extras</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {EXTRAS.map((x) => (
                          <label
                            key={x.id}
                            data-testid={`bespoke-extra-${x.id}`}
                            className={`flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-all duration-300 ${
                              form.extras.includes(x.id)
                                ? "border-gold-400 bg-gold-400/10"
                                : "border-white/10 hover:border-gold-400/40"
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="mt-1 accent-[#D4AF37]"
                              checked={form.extras.includes(x.id)}
                              onChange={() => toggleExtra(x.id)}
                            />
                            <span className="text-xs text-slate-300 font-light leading-relaxed">
                              {x.name} <span className="text-gold-300">+£{x.price}</span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <textarea required rows={5} placeholder="Tell us your story — the occasion, the people, the moments you want the song to hold…"
                      className={inputCls} value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="bespoke-story-input" />
                    <p className="text-xs text-gold-200/90 bg-gold-400/10 border border-gold-400/30 rounded-xl px-4 py-3 leading-relaxed" data-testid="bespoke-spam-note">
                      Important: after sending, please check your Spam/Junk folder for our confirmation email
                      and mark it "Not junk" / safe — this makes sure our reply about your song reaches you.
                    </p>
                    <div className="flex flex-wrap items-center gap-5">
                      <div className="flex gap-4 text-sm text-slate-300">
                        {["Email", "Phone"].map((m) => (
                          <label key={m} className="flex items-center gap-2 cursor-pointer">
                            <input type="radio" name="contact_pref" className="accent-[#D4AF37]"
                              checked={form.contact_pref === m}
                              onChange={() => setForm({ ...form, contact_pref: m })} data-testid={`bespoke-contact-${m.toLowerCase()}`} />
                            {m}
                          </label>
                        ))}
                      </div>
                      <button type="submit" disabled={sending} className="btn-gold ml-auto disabled:opacity-50" data-testid="bespoke-submit-btn">
                        {sending ? "Sending…" : `Send Enquiry — £${total}`} <ArrowRight size={15} />
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
